// PropertyPaymentsPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useLocation } from 'react-router-dom';
import axios from 'axios';
import './PropertySearchResults.css'; // Reuse existing styles
import './PropertyPayments.css'; // Reuse existing styles
import PropertyInfo from './PropertyInfo'; // Import PropertyInfo component

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_PROXY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// SI-1271 remove 2nd half button if 1st half is available
const norm = (s = "") =>
  s.toString().trim().toUpperCase().replace(/\s+/g, "_");

const getRawType = (p) => norm(p?.propertyTaxPaymentType || p?.description || "");

/**
 * Coerce various incoming values to a simple kind:
 *  'FIRST' | 'SECOND' | 'BOTH' | 'OTHER'
 */
const kindOf = (p) => {
  const t = getRawType(p);

  if (
    /^(FIRST_HALF|1ST_HALF|FIRSTHALF|HALF1)$/.test(t) ||
    /(FIRST.*HALF|1ST.*HALF)/.test(t)
  ) return "FIRST";

  if (
    /^(SECOND_HALF|2ND_HALF|SECONDHALF|HALF2)$/.test(t) ||
    /(SECOND.*HALF|2ND.*HALF)/.test(t)
  ) return "SECOND";

  if (
    /^(BOTH_HALVES|FULL_YEAR|ENTIRE_YEAR|TOTAL_YEAR|WHOLE_YEAR)$/.test(t) ||
    /(BOTH.*HALVES|FULL.*YEAR|ENTIRE.*YEAR|TOTAL.*YEAR|WHOLE.*YEAR)/.test(t)
  ) return "BOTH";

  return "OTHER";
};

const isFirstHalf = (p) => kindOf(p) === "FIRST";
const isSecondHalf = (p) => kindOf(p) === "SECOND";
const isBothHalves = (p) => kindOf(p) === "BOTH";

const prettyType = (p) => {
  const k = kindOf(p);
  if (k === "FIRST") return "1st Half";
  if (k === "SECOND") return "2nd Half";
  if (k === "BOTH") return "Both Halves";
  // fallback to original, but nicer
  const raw = p?.propertyTaxPaymentType || p?.description || "";
  return raw.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
};

const labelFor = (p) => {
  const k = kindOf(p);
  if (k === "FIRST") return "Pay 1st Half";
  if (k === "SECOND") return "Pay 2nd Half";
  if (k === "BOTH") return "Pay Both Halves";
  return "Pay";
};

const PropertyPaymentsPage = () => {
  const { taxid, taxyear } = useParams();
  const { dataProperty, dataClient } = useOutletContext();
  const { state } = useLocation();
  const payoffDate = state?.payoffDate ?? null;
  const [groupedPayments, setGroupedPayments] = useState(null);
  const [error, setError] = useState(null);
  const proxyUrl = process.env.REACT_APP_PROXY_URL;
  const isDev = process.env.REACT_APP_ENV === "development" ? true : false;
  
  useEffect(() => {
    if (!dataClient?.customer_number) return;

    const fetchData = async () => {
      try {
        const responsePaymentsDue = await apiClient.post(
          `/api/payments/due/${dataClient.customer_number}/${taxid}/${taxyear}`,
          { asOfDate: payoffDate }
        );
        // Get the payments data
        const paymentsData = Array.isArray(responsePaymentsDue.data.result)
        ? responsePaymentsDue.data.result
        : [];

        if (paymentsData.length === 0) {
          setGroupedPayments([]);
          return;
        }
        
        // …group-and-sort…
        
        // Group payments by PropertyTaxYear
        const grouped = paymentsData.reduce((acc, payment) => {
          const year = payment.propertyTaxYear;
            if (!acc[year]) acc[year] = [];
            acc[year].push(payment);
            return acc;
        }, {});

        // Sort + SI-1271 rule per year
      for (const year in grouped) {
        grouped[year] = grouped[year].slice().sort((a, b) => {
          const dateA = new Date(a.propertyTaxDate);
          const dateB = new Date(b.propertyTaxDate);
          return dateA - dateB;
        });

        const hasFirst = grouped[year].some(isFirstHalf);
        if (hasFirst) grouped[year] = grouped[year].filter((p) => !isSecondHalf(p));
      }

        // Convert the grouped object into an array of [year, payments] pairs, sorted by year
        // Convert to an array with metadata per year
        const groupedArray = Object.entries(grouped)
          .map(([year, payments]) => {
            const yearHasLien = payments.some(p => !!p.propertyTaxLien);
            return { year, payments, yearHasLien };
          })
          .sort((a, b) => Number(b.year) - Number(a.year));

        setGroupedPayments(groupedArray);
        
      } catch (error) {
        setError(error);
      }
    };

    fetchData();
  }, [dataClient, taxid, taxyear, payoffDate]);

  // WEST-955: a payment with no tax lines can't be posted to the county, so it can't be paid
  // online. The backend sends a heartbeat for these so support knows before the call.
  const hasTaxLines = (payment) => {
    const detail = payment?.propertyTaxlineDetail?.[0];
    return Array.isArray(detail?.rows) && detail.rows.length > 0;
  };

  const handlePay = async (payment) => {
   
    const confirmPayment = window.confirm(
      `Are you sure you want to pay $${payment.propertyTaxPayableAmount ?? payment.propertyTaxTotalAmount} for Tax ID ${payment.propertyTaxID}?`
    );
    if (!confirmPayment) {
      return;
    }
    if (!hasTaxLines(payment)) {
      return;
    }

    // Prepare paymentInfo object
    const paymentInfo = {
      token: payment.token || '', // Adjust as per your data structure
      customer_id: dataClient.customer_number, // Use dataClient for customer number
      payresult: 'processing',
      tax_id: payment.propertyTaxID,
      description: payment.description || 'Property Tax Payment', // Provide a default description
      taxline_detail: JSON.stringify(payment.propertyTaxlineDetail[0]), // Ensure this structure matches (hasTaxLines() guarantees rows)
      sku: payment.sku || '', // Adjust as needed
      ntotal_due: payment.propertyTaxTotalAmount,
      penalty: payment.propertyTaxPenalty,
      interest: payment.propertyTaxInterest,
      payment_type: payment.propertyTaxPaymentType || 'APS',
      payment_due_date: payment.propertyTaxDate,
      tax_year: payment.propertyTaxYear,
      isNumericTaxId: payment.propertyIsNumericTaxId,
    };

    try {
      const response = await fetch(`${proxyUrl}/api/payments/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentInfo),
      });

      if (response.ok) {
        const result = await response.json();
        const redirectUrl = result.transaction.paymentGateway.RedirectURL;
        const postObject = result.transaction.paymentGateway;

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = redirectUrl;

        for (const key in postObject) {
          if (postObject.hasOwnProperty(key)) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = key;
            hiddenField.value = postObject[key];
            form.appendChild(hiddenField);
          }
        }

        document.body.appendChild(form);
        const userConfirmed = window.confirm("Payment processing has been redirected to the credit card vendor site for secure payment options.");

        if (userConfirmed) {
          form.submit();
        } else {
          console.log('Form submission canceled by the user.');
        }
      } else {
        console.error('API call failed');
        setError('API call failed');
      }
    } catch (error) {
      console.error('Error making API call:', error);
      setError('Error making API call');
    }
  };

  if (error) return <div>Error: {error.message || error}</div>;

  if (!groupedPayments) {
    return <div>Loading payments...</div>;
  }

return (
  <div>
    <div>
      {/* Include the PropertyInfo component */}
      <PropertyInfo
        dataProperty={dataProperty}
        dataTax={[]} 
        proxyUrl={proxyUrl}
      />
    </div>

    {/* Clear floats */}
    <div style={{ clear: 'both' }}></div>

    <div className="property-search-results">
      {groupedPayments.length > 0 ? (
        <div className="payments-due">
          {groupedPayments.map(({ year, payments, yearHasLien }) => (
            <div key={year} className="year-group">

              <h2 className="taxYearHeading">Tax Year: {year}</h2>

              {yearHasLien ? (
                <div className="lien-message">
                  A tax lien exists for Tax Year {year}. Please contact your County Treasurer.
                </div>
              ) : (

                <ul className="results-list">
                  {payments.map((payment, index) => (
                    <li key={index} className="result-item">

                      {/* LEFT COLUMN */}
                      <div className="left-column">

                        <div>
                          <strong>Amount Due:</strong> ${payment.propertyTaxTotalAmount}
                        </div>

                        <div>
                          <strong>Due Date:</strong>{" "}
                          {new Date(payment.propertyTaxDate).toLocaleDateString()}
                        </div>

                        <div>
                          <strong>Payment Type:</strong> {payment.propertyTaxPaymentType}
                        </div>

                        <div>
                          <strong>Penalty:</strong> {payment.propertyTaxPenalty}
                        </div>

                        <div>
                          <strong>Interest:</strong> {payment.propertyTaxInterest}
                        </div>

                        {isDev && (
                          <>
                            <div>APS: {payment.propertyTaxAPSStatus ? "Yes" : "No"}</div>
                            <div>PTAP: {payment.propertyTaxPTAPStatus ? "Yes" : "No"}</div>
                          </>
                        )}

                        <div>Assignment: {payment.propertyTaxAssignment ? "Yes" : "No"}</div>
                        <div>Lien: {payment.propertyTaxLien ? "Yes" : "No"}</div>

                      </div>

                      {/* RIGHT COLUMN */}
                      <div className="right-column">

                        {payment.propertyTaxAssignment ? (

                          <span>
                            There is an Assignment on this property. Please Contact Your County Treasurer.
                          </span>

                        ) : payment.propertyTaxPaymentStatus === "PAID" ? (

                          <span>PAID</span>

                        ) : !hasTaxLines(payment) ? (

                          <span>
                            This payment can't be made online. Please contact your County Treasurer.
                          </span>

                        ) : dataClient.accept_delinquents === "1" &&
                          (parseFloat(payment.propertyTaxInterest) > 0 ||
                           parseFloat(payment.propertyTaxPenalty) > 0) ? (

                          <span>{dataClient.pay_tax_message}</span>

                        ) : (

                          <button
                            className="detail-link"
                            onClick={() => handlePay(payment)}
                          >
                            {labelFor(payment)}
                          </button>

                        )}

                      </div>

                    </li>
                  ))}
                </ul>

              )}

            </div>
          ))}
        </div>
      ) : (
        <div>No payments due.</div>
      )}
    </div>
  </div>
);    
};
export default PropertyPaymentsPage;
